import { FC, useEffect } from 'react';
import { Home, MapPin, X } from 'lucide-react';
import { RealEstateDto } from 'api/real-estate';
import {
  formatAddress,
  formatEuroPrice,
  formatPropertyType
} from 'features/subscription-process/home-insurance/utils/realEstateFormatters';

interface BienDetailDrawerProps {
  bien: RealEstateDto | null;
  onClose: () => void;
  /** Fonction de traduction (labels REAL_ESTATE.*) */
  t: (key: string) => string;
}

/**
 * Drawer latéral de détail d'un bien du parc client (consultation).
 * Même langage visuel que le résumé bien de la contractualisation.
 */
export const BienDetailDrawer: FC<BienDetailDrawerProps> = ({ bien, onClose, t }) => {
  // Fermeture via Échap
  useEffect(() => {
    if (!bien) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [bien, onClose]);

  if (!bien) return null;

  const detailRows: Array<{ label: string; value: string }> = [
    { label: 'Type de bien', value: formatPropertyType(bien.propertyType, t) || '—' },
    { label: 'Surface', value: bien.area ? `${bien.area} m²` : '—' },
    { label: 'Nombre de pièces', value: bien.roomCount != null ? String(bien.roomCount) : '—' },
    ...(bien.rentAmount != null
      ? [{ label: 'Loyer', value: formatEuroPrice(bien.rentAmount) }]
      : []),
    ...(bien.charges != null
      ? [{ label: 'Charges', value: formatEuroPrice(bien.charges) }]
      : [])
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
        {/* Hero */}
        <div className="relative h-48 bg-primary/80">
          {bien.photoUrl ? (
            <img
              src={bien.photoUrl}
              alt={bien.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-white">
              <Home size={64} className="opacity-50" />
            </div>
          )}
          <div className="absolute inset-0 bg-black bg-opacity-25" />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 rounded-full bg-white/90 p-1.5 text-gray-700 hover:bg-white"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h2 className="text-xl font-bold truncate">{bien.name || 'Bien immobilier'}</h2>
            <p className="text-sm opacity-90 flex items-center mt-1">
              <MapPin size={14} className="mr-1 shrink-0" />
              <span className="truncate">{formatAddress(bien.address)}</span>
            </p>
          </div>
        </div>

        {/* Caractéristiques */}
        <div className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Caractéristiques</h3>
          <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
            {detailRows.map(row => (
              <div key={row.label} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-gray-600">{row.label}</span>
                <span className="text-sm font-medium text-gray-900">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
